import React from 'react';

import { Badge, Group, Modal, Paper, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { IconCreditCard } from '@tabler/icons-react';
import { useLocalization } from 'context/localization-context';
import { BillingPlanWithInstallments, GymTitle } from 'types';

interface BillingPlanSelectProps {
  billingPlans: BillingPlanWithInstallments[];
  gym: GymTitle | undefined;
  selectedBillingPlanId?: string | null;
  onSelect: (billingPlan: BillingPlanWithInstallments) => void;
}

export function formatInstallmentsLine(billingPlan: BillingPlanWithInstallments): string {
  const installments = `${billingPlan.numberOfInstallments} Installment${
    billingPlan.numberOfInstallments > 1 ? 's' : ''
  }`;
  if (!billingPlan.installmentInterval) return installments;

  const count = billingPlan.installmentIntervalCount ?? 1;
  const interval = `${count} ${billingPlan.installmentInterval}${count > 1 ? 's' : ''}`;
  return `${installments} • ${interval}`;
}

// One selection list shared by the pre-checkout "Choose a billing plan" modal
// and the checkout accordion's Billing Plan step (spec Item 8).
const BillingPlanSelect = ({
  billingPlans,
  gym,
  selectedBillingPlanId,
  onSelect,
}: BillingPlanSelectProps): JSX.Element => {
  const theme = useMantineTheme();
  const { formatCurrency } = useLocalization(gym?.country, { isTaxInclusive: gym?.isTaxInclusive });

  return (
    <Stack spacing='sm' data-cy='billingPlanSelect'>
      {billingPlans.map(billingPlan => {
        const isSelected = billingPlan.billingPlanId === selectedBillingPlanId;
        return (
          <Paper
            key={billingPlan.billingPlanId}
            data-cy='billingPlanSelectOption'
            shadow='xs'
            radius='md'
            withBorder
            p='sm'
            sx={{
              cursor: 'pointer',
              borderColor: isSelected ? theme.colors.kiloblue?.[5] ?? theme.colors.blue[5] : undefined,
              borderWidth: isSelected ? 2 : 1,
              '&:hover': {
                backgroundColor: theme.colors.gray[0],
              },
            }}
            onClick={() => onSelect(billingPlan)}
          >
            <Group position='apart' noWrap>
              <Stack spacing={4}>
                <Group noWrap spacing='xs'>
                  <IconCreditCard />
                  <Title order={4} fw={600} truncate>
                    {billingPlan.name}
                  </Title>
                  {isSelected && (
                    <Badge color='green' variant='light' data-cy='billingPlanSelectedBadge'>
                      Selected
                    </Badge>
                  )}
                </Group>
                <Text color='dark.3' size='sm' truncate>
                  {formatInstallmentsLine(billingPlan)}
                </Text>
              </Stack>
              <Title order={4} fw={600}>
                {formatCurrency((billingPlan.totalPrice || 0) / 100)}
              </Title>
            </Group>
          </Paper>
        );
      })}
    </Stack>
  );
};

interface BillingPlanSelectModalProps extends BillingPlanSelectProps {
  opened: boolean;
  onClose: () => void;
}

export const BillingPlanSelectModal = ({
  opened,
  onClose,
  ...selectProps
}: BillingPlanSelectModalProps): JSX.Element => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Title order={4} color='dimmed'>
          Choose a billing plan
        </Title>
      }
      size='lg'
      centered
    >
      <BillingPlanSelect {...selectProps} />
    </Modal>
  );
};

export default BillingPlanSelect;
